import type { TranscriptionState } from '@/hooks/interview-session/types'

interface TranscriptPanelProps {
  transcription: TranscriptionState
  modelActive: boolean
  userActive: boolean
}

export function TranscriptPanel({ transcription, modelActive, userActive }: TranscriptPanelProps) {
  return (
    <div className="mb-8 space-y-4 min-h-[100px] flex flex-col justify-end">
      {transcription.model && (
        <div className="flex justify-start animate-fade-in-up">
          <TranscriptBubble
            active={modelActive}
            align="left"
            label="BO-SMART AI"
            text={transcription.model}
          />
        </div>
      )}

      {transcription.user && (
        <div className="flex justify-end animate-fade-in-up">
          <TranscriptBubble
            active={userActive}
            align="right"
            label="YOU"
            text={transcription.user}
          />
        </div>
      )}
    </div>
  )
}

interface TranscriptBubbleProps {
  active: boolean
  align: 'left' | 'right'
  label: string
  text: string
}

function TranscriptBubble({ active, align, label, text }: TranscriptBubbleProps) {
  const isUser = align === 'right'
  const baseClass =
    'relative px-6 py-4 rounded-2xl max-w-[85%] md:max-w-[75%] text-sm leading-relaxed backdrop-blur-md transition-all duration-300'
  const toneClass = isUser
    ? active
      ? 'bg-brand-900/90 border border-accent-400/50 shadow-[0_0_20px_-5px_rgba(250,204,21,0.2)]'
      : 'bg-brand-900/60 border border-brand-500/20'
    : active
      ? 'bg-dark-800/90 border border-brand-500/50 shadow-[0_0_20px_-5px_rgba(42,71,66,0.3)]'
      : 'bg-dark-800/60 border border-brand-500/10'

  return (
    <div className={`${baseClass} ${isUser ? 'rounded-tr-sm' : 'rounded-tl-sm'} ${toneClass}`}>
      {active && (
        <span className={`absolute -top-1 ${isUser ? '-right-1' : '-left-1'} flex h-3 w-3`}>
          <span
            className={`animate-ping absolute inline-flex h-full w-full rounded-full ${
              isUser ? 'bg-accent-400' : 'bg-brand-400'
            } opacity-75`}
          />
          <span
            className={`relative inline-flex rounded-full h-3 w-3 ${
              isUser ? 'bg-accent-500' : 'bg-brand-500'
            }`}
          />
        </span>
      )}
      <span
        className={`${isUser ? 'text-accent-400 text-right' : 'text-white'} text-xs font-extrabold tracking-wider block mb-1 uppercase`}
      >
        {label}
      </span>
      <p
        className={`text-gray-100 transition-colors ${isUser ? 'text-right' : ''} ${
          active ? 'text-white' : 'text-gray-200'
        }`}
      >
        {text}
      </p>
    </div>
  )
}
